import { useEffect, useRef } from "react"
import { cn } from "@/shared/lib/utils"
import { HapticLink } from "@/shared/ui/haptic-link"
import { Skeleton } from "@/shared/ui/skeleton"
import { TaxonomyCover } from "./taxonomy-cover"

export interface TaxonomyCardItem {
	id: string
	name: string
	iconUrl?: string | null
}

const ITEM_WIDTH = "w-[calc((min(100vw,48rem)-1.5rem)/4.4-0.75rem)]"

const RAIL =
	"hide-scrollbar -mx-3 snap-x overflow-x-auto scroll-px-3 px-3 sm:-mx-4 sm:scroll-px-4 sm:px-4"

interface TaxonomyRailProps {
	items: TaxonomyCardItem[]
	selectedId?: string
	getPath: (item: TaxonomyCardItem) => string
	className?: string
}

export function TaxonomyRail({ items, selectedId, getPath, className }: TaxonomyRailProps) {
	const railRef = useRef<HTMLDivElement>(null)

	useEffect(() => {
		if (!selectedId) return
		const current = railRef.current?.querySelector<HTMLElement>('[data-selected="true"]')
		current?.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" })
	}, [selectedId])

	return (
		<div ref={railRef} className={cn(RAIL, className)}>
			<div className="flex w-max gap-3 py-1">
				{items.map((item) => {
					const selected = item.id === selectedId
					return (
						<HapticLink
							key={item.id}
							to={getPath(item)}
							data-selected={selected}
							aria-current={selected ? "page" : undefined}
							className={cn(
								"group flex shrink-0 snap-start flex-col items-center gap-1.5 focus-visible:outline-none",
								ITEM_WIDTH,
							)}
						>
							<span
								className={cn(
									"relative flex size-16 items-center justify-center overflow-hidden rounded-full bg-card ring-1 ring-white/10 transition-transform duration-150 ease-[var(--ease-out-soft)] group-active:scale-95 group-focus-visible:ring-2 group-focus-visible:ring-ring",
									selected && "ring-2 ring-primary",
								)}
							>
								<TaxonomyCover iconUrl={item.iconUrl} fallbackClassName="size-6" />
							</span>
							<span
								className={cn(
									"line-clamp-2 text-center text-xs leading-tight font-medium",
									selected && "text-primary",
								)}
							>
								{item.name}
							</span>
						</HapticLink>
					)
				})}
			</div>
		</div>
	)
}

export function TaxonomyRailSkeleton({ className }: { className?: string }) {
	return (
		<div className={cn(RAIL, className)}>
			<div className="flex w-max gap-3 py-1">
				{Array.from({ length: 5 }).map((_, index) => (
					<div
						key={index}
						className={cn("flex shrink-0 flex-col items-center gap-1.5", ITEM_WIDTH)}
					>
						<Skeleton className="size-16 rounded-full" />
						<Skeleton className="h-2.5 w-12" />
					</div>
				))}
			</div>
		</div>
	)
}
